import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { SCHOOL_LEVELS, SECONDARY_SCHOOL_LEVELS, isCpgeLevel } from '@jeraisonne/db';
import type { SupabaseClient } from '@supabase/supabase-js';
import type { SchoolLevel } from '@jeraisonne/db';
import { Button, Alert, AlertDescription, cn } from '@jeraisonne/ui';
import { supabase } from '@/lib/supabase';
import { addOneMonth, levelChangeStatus, formatLevelChangeDate } from '@/lib/level-change';
import { CPGE_SCHOOL_LEVELS, cpgeStudyYear } from '@/lib/cpge-profile';

const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

interface StudentLevelControlProps {
  userId: string;
  currentLevel: SchoolLevel | null;
  /** `student_profiles.level_changed_at` (ISO) ou null si jamais changé. */
  levelChangedAt: string | null;
  /** `student_profiles.formation_type` : un étudiant `cpge` choisit parmi les filières de prépa. */
  formationType?: string | null;
}

/**
 * Changement du niveau scolaire par l'élève, avec cooldown d'un mois (migration 0151).
 * Le cooldown est réellement enforced par le trigger Postgres ; ici on gâte juste
 * l'UI et on affiche la date de prochaine modification.
 */
export function StudentLevelControl({
  userId,
  currentLevel,
  levelChangedAt,
  formationType = null,
}: StudentLevelControlProps) {
  // Prépa (migration 0302) : MPSI/PTSI/MP/PT ; sinon les classes du secondaire.
  const isCpge = formationType === 'cpge' || isCpgeLevel(currentLevel);
  const options = isCpge ? CPGE_SCHOOL_LEVELS : SECONDARY_SCHOOL_LEVELS;
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [selected, setSelected] = useState<SchoolLevel | null>(currentLevel);

  const status = levelChangeStatus(levelChangedAt, new Date());
  // Si l'élève change maintenant, il sera verrouillé jusqu'à cette date.
  const lockedUntilIfChanged = addOneMonth(new Date());

  const changeMutation = useMutation({
    mutationFn: async (level: SchoolLevel) => {
      // `study_year` (0237) est postérieur aux types générés → client générique.
      const year = cpgeStudyYear(level);
      const patch = year === null ? { level } : { level, study_year: year };
      const { error } = await (supabase as unknown as SupabaseClient)
        .from('student_profiles')
        .update(patch)
        .eq('id', userId);
      if (error) {
        if (error.message.includes('level_change_cooldown')) {
          throw new Error('Tu as déjà changé ton niveau ce mois-ci. Réessaie plus tard.');
        }
        throw new Error(error.message);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['student-profile', userId] });
      setEditing(false);
    },
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <span className="text-ink-600 text-sm">Niveau</span>
        <div className="flex items-center gap-2">
          <span className="text-ink-900 text-sm font-semibold">
            {currentLevel ? (LEVEL_LABEL[currentLevel] ?? currentLevel) : '—'}
          </span>
          {!editing && status.allowed && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setSelected(currentLevel);
                setEditing(true);
              }}
            >
              Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Cooldown actif : pas d'édition possible */}
      {!editing && !status.allowed && status.nextChangeAt && (
        <p className="text-ink-400 text-xs italic">
          Modifiable à partir du {formatLevelChangeDate(status.nextChangeAt)}.
        </p>
      )}

      {/* Mode édition */}
      {editing && (
        <div className="border-ink-100 space-y-3 rounded-xl border bg-white p-3">
          <div
            className={cn(
              'grid gap-2',
              isCpge ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-4 sm:grid-cols-7',
            )}
          >
            {options.map((l) => (
              <button
                key={l.value}
                type="button"
                onClick={() => setSelected(l.value)}
                className={cn(
                  'min-h-[2.75rem] rounded-xl border-2 p-3 text-sm font-semibold transition-all active:scale-[0.98]',
                  selected === l.value
                    ? 'border-primary-500 bg-primary-50 text-primary-700'
                    : 'border-ink-100 text-ink-700 hover:border-primary-300 bg-white',
                )}
              >
                {l.label}
              </button>
            ))}
          </div>

          <p className="text-ink-500 text-xs">
            En changeant maintenant, tu ne pourras plus modifier ton niveau avant le{' '}
            <span className="font-semibold">{formatLevelChangeDate(lockedUntilIfChanged)}</span>.
          </p>

          {changeMutation.isError && (
            <Alert variant="error">
              <AlertDescription>
                {(changeMutation.error as Error)?.message ?? 'Une erreur est survenue.'}
              </AlertDescription>
            </Alert>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => {
                setEditing(false);
                changeMutation.reset();
              }}
              disabled={changeMutation.isPending}
            >
              Annuler
            </Button>
            <Button
              type="button"
              size="sm"
              className="w-full sm:w-auto"
              disabled={changeMutation.isPending || !selected || selected === currentLevel}
              onClick={() => selected && changeMutation.mutate(selected)}
            >
              {changeMutation.isPending ? 'Enregistrement…' : 'Confirmer le niveau'}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
